import { CheckCircle2, Loader2, PauseCircle, XCircle } from 'lucide-react';
import { FOREST_GREEN, MID_GRAY, ERROR, BORDER, SHADOWS, WHITE } from '@/styles/tokens';
import { PIPELINE_STAGE_LABELS } from '@/types/plan';
import type { PlanPipelineStage, PhasePrompt, AuditResult } from '@/types/plan';
import { usePlanStore } from '@/stores/ai/plan-store';
import { useTick } from '@/hooks/useTick';
import { formatDuration } from '@/utils';

// Ordered pipeline stages for comparison
const STAGE_ORDER: PlanPipelineStage[] = [
  'idle', 'conversing', 'researching',
  'generating_roadmap', 'auditing_roadmap',
  'decomposing', 'auditing_phases',
  'reviewing',
  'executing_phase', 'auditing_implementation',
  'final_audit', 'complete', 'failed',
];

// Ordered list of all known pipeline sub-steps; the UI dynamically shows
// whichever steps the backend actually emits SSE events for.
const KNOWN_STEP_ORDER = ['Data Discovery', 'Schema Creation', 'Layout', 'Components', 'Data Binding', 'Styles'];
const DEFAULT_STEPS = ['Layout', 'Components', 'Styles'];

function getStageIndex(stage: PlanPipelineStage): number {
  return STAGE_ORDER.indexOf(stage);
}

type StageStatus = 'completed' | 'active' | 'pending';

function getStageStatus(stage: PlanPipelineStage, currentStage: PlanPipelineStage): StageStatus {
  if (currentStage === 'complete') return 'completed';
  if (currentStage === 'failed') {
    return getStageIndex(stage) < getStageIndex(currentStage) ? 'completed' : 'pending';
  }
  const stageIdx = getStageIndex(stage);
  const currentIdx = getStageIndex(currentStage);
  if (stageIdx < currentIdx) return 'completed';
  if (stageIdx === currentIdx) return 'active';
  return 'pending';
}

function StatusIcon({ status }: { status: StageStatus }) {
  switch (status) {
    case 'completed':
      return <CheckCircle2 className="w-4 h-4" style={{ color: FOREST_GREEN.DEFAULT }} />;
    case 'active':
      return <Loader2 className="w-4 h-4 animate-spin" style={{ color: FOREST_GREEN.DEFAULT }} />;
    case 'pending':
      return <PauseCircle className="w-4 h-4" style={{ color: MID_GRAY.lighter }} />;
  }
}

const TABULAR_NUMS: React.CSSProperties = { fontVariantNumeric: 'tabular-nums' };

function getElapsed(
  timing: { startedAt: number; endedAt: number | null } | undefined,
  now: number,
): string | null {
  if (!timing) return null;
  const elapsed = (timing.endedAt ?? now) - timing.startedAt;
  if (elapsed < 0) return null;
  return formatDuration(elapsed);
}

interface PlanPipelineProgressProps {
  pipelineStage: PlanPipelineStage;
  pipelineProgress: number;
  phases: PhasePrompt[];
  currentPhaseId?: string;
  implementationAudits: Record<string, AuditResult>;
  finalAudit?: AuditResult;
  errors: Record<string, string>;
  onPause: () => void;
  onCancel: () => void;
}

export function PlanPipelineProgress({
  pipelineStage,
  pipelineProgress,
  phases,
  currentPhaseId,
  implementationAudits,
  finalAudit,
  errors,
  onPause,
  onCancel,
}: PlanPipelineProgressProps) {
  // Timing state from plan store
  const pipelineStartedAt = usePlanStore((s) => s.pipelineStartedAt);
  const stageTimings = usePlanStore((s) => s.stageTimings);
  const subStepTimings = usePlanStore((s) => s.subStepTimings);

  // Single shared tick for all timers
  const isPipelineActive = pipelineStage !== 'complete' && pipelineStage !== 'failed' && pipelineStage !== 'idle';
  const now = useTick(isPipelineActive);

  // Filter out 'idle' and 'failed' from display
  const displayStages = STAGE_ORDER.filter((s) => s !== 'idle' && s !== 'failed');

  // Overall elapsed
  const overallElapsed = pipelineStartedAt ? formatDuration(now - pipelineStartedAt) : null;

  return (
    <div
      aria-live="polite"
      style={{
        border: `1px solid ${BORDER.DEFAULT}`,
        borderRadius: 8,
        background: WHITE,
        boxShadow: SHADOWS.sm,
        padding: 16,
      }}
    >
      {/* Header with overall timer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>Plan Pipeline — Full Mode</span>
        {overallElapsed && (
          <span style={{ fontSize: 12, color: MID_GRAY.DEFAULT, fontWeight: 400, ...TABULAR_NUMS }}>
            {overallElapsed}
          </span>
        )}
      </div>

      {/* Progress bar */}
      <div
        role="progressbar"
        aria-valuenow={pipelineProgress}
        aria-valuemin={0}
        aria-valuemax={100}
        style={{
          height: 6,
          borderRadius: 3,
          background: MID_GRAY.lighter,
          marginBottom: 16,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pipelineProgress}%`,
            height: '100%',
            borderRadius: 3,
            background: FOREST_GREEN.DEFAULT,
            transition: 'width 0.5s ease',
          }}
        />
      </div>
      <div style={{ fontSize: 11, color: MID_GRAY.DEFAULT, marginBottom: 12, textAlign: 'right' }}>
        {pipelineProgress}%
      </div>

      {/* Stage list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {displayStages.map((stage) => {
          const status = getStageStatus(stage, pipelineStage);
          const label = PIPELINE_STAGE_LABELS[stage];

          // Additional context for certain stages
          let suffix = '';
          if (stage === 'executing_phase' && status === 'active') {
            const completedCount = Object.keys(implementationAudits).length;
            const displayIndex = Math.min(completedCount + 1, phases.length);
            suffix = ` ${displayIndex}/${phases.length}`;
          }
          if (stage === 'final_audit' && status === 'completed' && finalAudit) {
            suffix = ` ${finalAudit.overallScore}/100`;
          }

          // Inline audit scores for completed audit stages
          let auditScore: string | null = null;
          if (stage === 'auditing_roadmap' && status === 'completed') {
            auditScore = 'done';
          }
          if (stage === 'auditing_phases' && status === 'completed') {
            const scores = Object.values(implementationAudits).map((a) => a.overallScore);
            if (scores.length > 0) {
              auditScore = `avg ${Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)}`;
            } else {
              auditScore = 'done';
            }
          }

          // Per-stage elapsed time
          const stageElapsed = getElapsed(stageTimings[stage], now);

          // Build status text: audit score takes precedence, then timer, then fallback
          let statusText = '';
          if (status === 'completed') {
            statusText = auditScore ?? stageElapsed ?? 'done';
          } else if (status === 'active') {
            statusText = stageElapsed ?? '';
          }

          return (
            <div key={stage}>
              <div
                className="flex items-center gap-2"
                style={{ padding: '4px 0' }}
                aria-label={`${label}: ${status === 'completed' ? 'completed' : status === 'active' ? 'in progress' : 'pending'}${auditScore ? `, score ${auditScore}` : ''}`}
              >
                <StatusIcon status={status} />
                <span
                  style={{
                    flex: 1,
                    fontSize: 13,
                    fontWeight: status === 'active' ? 600 : 400,
                    color: status === 'pending' ? MID_GRAY.lighter : undefined,
                  }}
                >
                  {label}{suffix}
                </span>
                {statusText && (
                  <span style={{ fontSize: 11, color: MID_GRAY.lighter, ...TABULAR_NUMS }}>
                    {statusText}
                  </span>
                )}
              </div>

              {/* Nested sub-agent progress for executing_phase */}
              {stage === 'executing_phase' && status === 'active' && (() => {
                // Find the phase with active sub-step data (visual-editor phases emit SSE events;
                // data/liaison phases don't, so currentPhaseId may point to the wrong one)
                const activePhaseId = (() => {
                  if (currentPhaseId && subStepTimings[`${currentPhaseId}:Layout`]) {
                    return currentPhaseId;
                  }
                  for (const key of Object.keys(subStepTimings)) {
                    const [phaseId] = key.split(':');
                    const timing = subStepTimings[key];
                    if (timing && timing.endedAt === null) return phaseId;
                  }
                  return currentPhaseId;
                })();

                if (!activePhaseId) return null;

                // Derive sub-step status from plan store timing data
                const getSubStepStatus = (stepName: string): StageStatus => {
                  const timing = subStepTimings[`${activePhaseId}:${stepName}`];
                  if (!timing) return 'pending';
                  if (timing.endedAt !== null) return 'completed';
                  return 'active';
                };

                // Dynamically derive visible steps from timing data
                const activeSteps = KNOWN_STEP_ORDER.filter(step =>
                  subStepTimings[`${activePhaseId}:${step}`]
                );
                const stepsToShow = activeSteps.length > 0 ? activeSteps : DEFAULT_STEPS;

                return (
                  <div style={{ paddingLeft: 28, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {stepsToShow.map((stepName: string, i: number) => {
                      const subStatus = getSubStepStatus(stepName);
                      const isLast = i === stepsToShow.length - 1;
                      const subElapsed = getElapsed(subStepTimings[`${activePhaseId}:${stepName}`], now);

                      let subStatusText = '';
                      if (subStatus === 'completed') {
                        subStatusText = subElapsed ?? 'done';
                      } else if (subStatus === 'active') {
                        subStatusText = subElapsed ?? '';
                      } else {
                        subStatusText = 'pending';
                      }

                      return (
                        <div
                          key={stepName}
                          className="flex items-center gap-2"
                          style={{ fontSize: 12, color: subStatus === 'pending' ? MID_GRAY.lighter : MID_GRAY.DEFAULT }}
                        >
                          <span style={{ color: MID_GRAY.lighter }}>
                            {isLast ? '└──' : '├──'}
                          </span>
                          <StatusIcon status={subStatus} />
                          <span>{stepName}</span>
                          <span style={{ fontSize: 11, color: MID_GRAY.lighter, marginLeft: 'auto', ...TABULAR_NUMS }}>
                            {subStatusText}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>

      {/* Errors */}
      {Object.keys(errors).length > 0 && (
        <div
          className="flex items-start gap-2"
          style={{
            marginTop: 12,
            padding: '8px 12px',
            borderRadius: 6,
            background: ERROR.bg,
            fontSize: 12,
          }}
        >
          <XCircle className="w-4 h-4 flex-shrink-0" style={{ color: ERROR.DEFAULT, marginTop: 1 }} />
          <div>
            {Object.entries(errors).map(([phaseId, err]) => (
              <div key={phaseId} style={{ color: ERROR.DEFAULT, lineHeight: 1.5 }}>{err}</div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2" style={{ marginTop: 16 }}>
        <button
          type="button"
          onClick={onPause}
          aria-label="Pause pipeline at next checkpoint"
          style={{
            padding: '8px 20px',
            borderRadius: 6,
            border: `1px solid ${FOREST_GREEN.DEFAULT}`,
            background: 'transparent',
            color: FOREST_GREEN.DEFAULT,
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          Pause
        </button>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel and abort the pipeline"
          style={{
            padding: '8px 20px',
            borderRadius: 6,
            border: `1px solid ${ERROR.DEFAULT}`,
            background: 'transparent',
            color: ERROR.DEFAULT,
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
